import React from 'react';
import PrivacyPolicy from '../../components/PrivacyPolicy/PrivacyPolicy';

function LayovraPrivacy() {
  const sections = [
    {
      title: 'Introduction',
      content: (
        <>
          <p>
            Layovra ("we," "our," or "the Service") is an AI-powered furniture discovery,
            layout planning, and visualization platform operated by Castle Point Innovations,
            a division of Cuitt LLC. This Privacy Policy explains how we collect, use, store,
            and protect your information when you use our Service.
          </p>
          <p>
            By using Layovra, you agree to the collection and use of information as described
            in this policy. If you do not agree with this policy, please do not use the Service.
          </p>
        </>
      ),
    },
    {
      title: 'Information We Collect',
      content: (
        <>
          <p>We collect the following categories of information:</p>
          <p><strong>Account Information</strong></p>
          <ul>
            <li>Email address (collected through Firebase Authentication)</li>
            <li>Account credentials and authentication tokens</li>
          </ul>
          <p><strong>Project and Collection Data</strong></p>
          <ul>
            <li>Projects and collections you create within the Service</li>
            <li>Furniture items you save, organize, or add to collections</li>
            <li>User-generated room layouts and design configurations</li>
          </ul>
          <p><strong>Furniture and Design Data</strong></p>
          <ul>
            <li>Furniture search queries you enter</li>
            <li>Furniture images (both original source images and your selections)</li>
            <li>Furniture dimensions and specifications</li>
            <li>3D skeleton models, including JSON definitions and GLB mesh files</li>
          </ul>
          <p><strong>Usage Data</strong></p>
          <ul>
            <li>Search history</li>
            <li>File uploads, including 3D models and design files</li>
          </ul>
        </>
      ),
    },
    {
      title: 'How We Use Your Information',
      content: (
        <>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve the Service</li>
            <li>Authenticate your identity and manage your account</li>
            <li>Process furniture search queries and return relevant results</li>
            <li>Generate and store 3D skeleton models based on your furniture selections</li>
            <li>Save your projects, collections, layouts, and design preferences</li>
            <li>Analyze usage patterns to improve Service functionality and performance</li>
            <li>Communicate with you about Service updates or account-related matters</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Third-Party Services',
      content: (
        <>
          <p>
            Layovra relies on the following third-party services to operate. Each service
            has its own privacy policy governing its handling of your data:
          </p>
          <ul>
            <li>
              <strong>Firebase (Google)</strong> — Authentication, Firestore database,
              Cloud Storage, and Hosting. Used to manage accounts, store project data,
              and host uploaded files.
            </li>
            <li>
              <strong>Google Cloud (Compute Engine)</strong> — Backend infrastructure
              for processing requests and generating 3D models.
            </li>
            <li>
              <strong>Google Generative AI (Gemini 2.0 Flash)</strong> — AI-powered
              analysis of furniture images to extract dimensions and generate 3D
              skeleton model definitions.
            </li>
            <li>
              <strong>SerpAPI</strong> — Furniture image search. Search queries you
              enter are sent to SerpAPI to retrieve relevant furniture images and
              product information.
            </li>
          </ul>
          <p>
            We recommend reviewing the privacy policies of these third-party providers
            to understand how they process your data.
          </p>
        </>
      ),
    },
    {
      title: 'Data Storage and Security',
      content: (
        <>
          <p>
            Your data is stored using Firebase Firestore and Firebase Cloud Storage,
            hosted on Google Cloud infrastructure. We implement reasonable technical
            and organizational safeguards to protect your information, including:
          </p>
          <ul>
            <li>Firebase Authentication for secure account access</li>
            <li>Firestore security rules to restrict data access to authorized users</li>
            <li>Encrypted data transmission via HTTPS</li>
            <li>Cloud Storage access controls for uploaded files and generated models</li>
          </ul>
          <p>
            While we take reasonable measures to protect your data, no method of
            electronic storage or transmission is completely secure. We cannot
            guarantee absolute security.
          </p>
        </>
      ),
    },
    {
      title: 'Data Retention',
      content: (
        <>
          <p>
            We retain your data for as long as your account is active or as needed to
            provide the Service. This includes your projects, collections, search history,
            uploaded files, and generated 3D models.
          </p>
          <p>
            If you delete your account, we will make reasonable efforts to remove your
            personal data from our systems within a reasonable timeframe. Some data may
            be retained in backups or logs for a limited period as required for
            operational or legal purposes.
          </p>
        </>
      ),
    },
    {
      title: 'Your Rights',
      content: (
        <>
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate personal data</li>
            <li>Request deletion of your personal data</li>
            <li>Export your data in a portable format</li>
            <li>Withdraw consent where processing is based on consent</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the information
            provided in the Contact Us section below.
          </p>
        </>
      ),
    },
    {
      title: "Children's Privacy",
      content: (
        <>
          <p>
            Layovra is not intended for use by children under the age of 13. We do not
            knowingly collect personal information from children under 13. If we become
            aware that we have collected personal data from a child under 13, we will
            take steps to delete that information promptly. If you believe a child under
            13 has provided us with personal data, please contact us immediately.
          </p>
        </>
      ),
    },
    {
      title: 'Changes to This Policy',
      content: (
        <>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in
            our practices or for legal, operational, or regulatory reasons. When we make
            changes, we will update the "Last Updated" date at the top of this page. We
            encourage you to review this policy periodically. Your continued use of the
            Service after any changes constitutes acceptance of the updated policy.
          </p>
        </>
      ),
    },
    {
      title: 'Contact Us',
      content: (
        <>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy
            or our data practices, please contact us at:
          </p>
          <p>
            <strong>Email:</strong>{' '}
            <a href="mailto:contact@castlepointinnovations.com">
              contact@castlepointinnovations.com
            </a>
          </p>
          <p>Castle Point Innovations — Cuitt LLC</p>
        </>
      ),
    },
  ];

  return <PrivacyPolicy productName="Layovra" lastUpdated="March 27, 2026" sections={sections} />;
}

export default LayovraPrivacy;
